import { Link } from 'react-router-dom';
import { EmptyState, Pill, type PillTone } from '../../index.js';

/**
 * When a campaign's events happen: a date axis from the first to the last,
 * month ticks, a line for today, and one row per event drawn as a bar from its
 * start to its end, colored by its stage, with the details on hover. Events
 * with no date yet are listed under the axis so nothing is hidden. An optional
 * band shades a span (a campaign's delivery window), and an event can carry a
 * flag the caller decides on (such as falling outside that window).
 */

/** One event on the timeline. */
export interface TimelineEvent {
  readonly id: string;
  readonly title: string;
  /** Who is running it: the team, the city, the venue. */
  readonly subtitle: string | null;
  /** When it starts; null when the date is not set yet. */
  readonly startAt: string | null;
  /** When it ends, when known; the bar spans start to end. */
  readonly endAt?: string | null | undefined;
  readonly stage: { readonly label: string; readonly tone: PillTone };
  /** Where the event's own page is; absent when there is nowhere to go. */
  readonly href?: string | undefined;
  /** A warning shown beside the event, e.g. "Outside the delivery window". */
  readonly flag?: string | undefined;
}

/** A span shaded across the axis, from one calendar date to another, inclusive. */
export interface TimelineBand {
  /** ISO date or timestamp; only the date is read. */
  readonly startAt: string;
  readonly endAt: string;
  readonly label: string;
}

export interface CampaignTimelineProps {
  readonly events: readonly TimelineEvent[];
  /** The clock, injected so the today line is testable. */
  readonly now?: Date;
  /** Shaded behind the events, e.g. the delivery window. */
  readonly band?: TimelineBand | undefined;
  readonly emptyTitle?: string;
  readonly emptyMessage?: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;
/** Breathing room either side of the first and last event. */
const PAD_DAYS = 7;

/** Where a moment sits on the axis, as a percentage from the left. */
function positionOf(at: number, start: number, end: number): number {
  if (end <= start) return 50;
  return Math.min(100, Math.max(0, ((at - start) / (end - start)) * 100));
}

/** The first day of each month the axis crosses. */
function monthTicks(start: number, end: number): readonly { readonly at: number; readonly label: string }[] {
  const ticks: { at: number; label: string }[] = [];
  const cursor = new Date(start);
  cursor.setUTCDate(1);
  cursor.setUTCHours(0, 0, 0, 0);
  cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  while (cursor.getTime() < end) {
    ticks.push({ at: cursor.getTime(), label: cursor.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' }) });
    cursor.setUTCMonth(cursor.getUTCMonth() + 1);
  }
  return ticks;
}

/** Solid bar colors, one per stage tone; the legend names what each means. */
const BAR_TONES: Readonly<Record<PillTone, string>> = {
  success: 'bg-emerald-500',
  warning: 'bg-amber-400',
  danger: 'bg-rose-500',
  brand: 'bg-brand',
  info: 'bg-violet-500',
  neutral: 'bg-slate-400',
};
/** A bar never draws narrower than this, so a three-hour program is still visible. */
const MIN_BAR_PX = 10;
/** How close, as a share of the axis, a month label may sit to the Today label. */
const TICK_CLEARANCE_PCT = 6;

/** The stages on the timeline, once each, in the order they first appear. */
function legendOf(events: readonly TimelineEvent[]): readonly TimelineEvent['stage'][] {
  const seen = new Map<string, TimelineEvent['stage']>();
  for (const event of events) if (!seen.has(event.stage.label)) seen.set(event.stage.label, event.stage);
  return [...seen.values()];
}

/** Local midnight of an ISO date's calendar day, so a date stored as UTC midnight is not a day early. */
function dayStart(iso: string): number {
  return new Date(`${iso.slice(0, 10)}T00:00:00`).getTime();
}

function longDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function CampaignTimeline({
  events,
  now = new Date(),
  band,
  emptyTitle = 'No events yet',
  emptyMessage = 'Events appear here on a timeline once they are linked to the campaign.',
}: CampaignTimelineProps): React.ReactElement {
  const dated = events
    .filter((event): event is TimelineEvent & { readonly startAt: string } => event.startAt !== null)
    .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());
  const undated = events.filter((event) => event.startAt === null);

  if (events.length === 0) return <EmptyState title={emptyTitle} message={emptyMessage} />;

  // Today is always on the axis, so the line shows how far off the events are
  // (or how long ago), rather than vanishing when they are all ahead of it.
  const bandStart = band ? dayStart(band.startAt) : null;
  const bandEnd = band ? dayStart(band.endAt) + DAY_MS : null;
  const times = [
    ...dated.flatMap((event) => [new Date(event.startAt).getTime(), ...(event.endAt ? [new Date(event.endAt).getTime()] : [])]),
    now.getTime(),
    ...(bandStart === null || bandEnd === null ? [] : [bandStart, bandEnd]),
  ];
  const first = Math.min(...times);
  const last = Math.max(...times);
  const start = first - PAD_DAYS * DAY_MS;
  const end = last + PAD_DAYS * DAY_MS;
  const today = now.getTime();
  const todayVisible = today >= start && today <= end;
  // A month label right beside "Today" would print over it; today wins.
  const ticks = monthTicks(start, end).filter(
    (tick) => !todayVisible || Math.abs(positionOf(tick.at, start, end) - positionOf(today, start, end)) > TICK_CLEARANCE_PCT,
  );

  const titleOf = (event: TimelineEvent): React.ReactNode =>
    event.href ? (
      <Link to={event.href} className="font-bold text-brand hover:underline">
        {event.title}
      </Link>
    ) : (
      <span className="font-bold">{event.title}</span>
    );

  return (
    <div className="space-y-6">
      {dated.length > 0 ? (
        <div>
          <div className="relative ml-[220px] h-8 border-b border-portal-line text-portal-micro text-portal-muted" aria-hidden>
            {ticks.map((tick) => (
              <span key={tick.at} className="absolute bottom-1 -translate-x-1/2 whitespace-nowrap" style={{ left: `${positionOf(tick.at, start, end)}%` }}>
                {tick.label}
              </span>
            ))}
            {todayVisible ? (
              <span className="absolute bottom-1 -translate-x-1/2 whitespace-nowrap font-bold text-brand" style={{ left: `${positionOf(today, start, end)}%` }}>
                Today
              </span>
            ) : null}
          </div>
          <ol className="relative divide-y divide-portal-line">
            {band && bandStart !== null && bandEnd !== null ? (
              <span
                className="pointer-events-none absolute top-0 bottom-0 bg-portal-sky"
                style={{
                  left: `calc(220px + (100% - 220px) * ${positionOf(bandStart, start, end) / 100})`,
                  width: `calc((100% - 220px) * ${(positionOf(bandEnd, start, end) - positionOf(bandStart, start, end)) / 100})`,
                }}
                title={band.label}
                aria-hidden
              />
            ) : null}
            {todayVisible ? (
              <span
                className="pointer-events-none absolute bottom-0 top-0 z-10 w-px bg-brand"
                style={{ left: `calc(220px + (100% - 220px) * ${positionOf(today, start, end) / 100})` }}
                aria-hidden
              />
            ) : null}
            {dated.map((event) => {
              const at = new Date(event.startAt).getTime();
              const until = event.endAt ? Math.max(at, new Date(event.endAt).getTime()) : at;
              const left = positionOf(at, start, end);
              const width = positionOf(until, start, end) - left;
              const dates =
                event.endAt && longDate(event.endAt) !== longDate(event.startAt)
                  ? `${longDate(event.startAt)} to ${longDate(event.endAt)}`
                  : longDate(event.startAt);
              const summary = [event.title, dates, event.stage.label, event.flag].filter(Boolean).join(', ');
              return (
                <li key={event.id} className="grid grid-cols-[220px_1fr] items-center gap-3 py-3">
                  <div className="min-w-0 pr-3">
                    <p className="truncate text-portal-small">{titleOf(event)}</p>
                    {event.subtitle ? <p className="truncate text-portal-micro text-portal-muted">{event.subtitle}</p> : null}
                  </div>
                  <div className="relative h-8">
                    <span
                      tabIndex={0}
                      aria-label={summary}
                      className={`group absolute top-1/2 h-4 -translate-y-1/2 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 ${BAR_TONES[event.stage.tone]} ${event.flag ? 'ring-2 ring-rose-500 ring-offset-1' : ''}`}
                      style={{ left: `${left}%`, width: `max(${MIN_BAR_PX}px, ${width}%)` }}
                    >
                      <span
                        role="tooltip"
                        className={`pointer-events-none absolute bottom-full z-20 mb-2 hidden w-max max-w-[280px] rounded-portal-button border border-portal-line bg-white p-3 text-left shadow-portal-button group-hover:block group-focus:block ${left > 60 ? 'right-0' : 'left-0'}`}
                      >
                        <span className="block text-portal-small font-bold text-portal-ink">{event.title}</span>
                        {event.subtitle ? <span className="block text-portal-micro text-portal-muted">{event.subtitle}</span> : null}
                        <span className="mt-1 block text-portal-micro text-portal-ink">{dates}</span>
                        <span className="mt-2 flex flex-wrap gap-1">
                          <Pill tone={event.stage.tone}>{event.stage.label}</Pill>
                          {event.flag ? <Pill tone="warning">{event.flag}</Pill> : null}
                        </span>
                      </span>
                    </span>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      ) : null}

      {dated.length > 0 ? (
        <ul className="flex flex-wrap items-center gap-x-5 gap-y-2 text-portal-micro text-portal-muted" aria-label="Key">
          {legendOf(dated).map((stage) => (
            <li key={stage.label} className="flex items-center gap-2">
              <span aria-hidden className={`inline-block h-2.5 w-5 rounded-full ${BAR_TONES[stage.tone]}`} />
              {stage.label}
            </li>
          ))}
          {dated.some((event) => event.flag) ? (
            <li className="flex items-center gap-2">
              <span aria-hidden className="inline-block h-2.5 w-5 rounded-full bg-white ring-2 ring-rose-500" />
              {dated.find((event) => event.flag)?.flag}
            </li>
          ) : null}
          {band ? (
            <li className="flex items-center gap-2">
              <span aria-hidden className="inline-block size-3 rounded-sm bg-portal-sky" />
              {band.label}: {longDate(`${band.startAt.slice(0, 10)}T12:00:00`)} to {longDate(`${band.endAt.slice(0, 10)}T12:00:00`)}
            </li>
          ) : null}
        </ul>
      ) : null}

      {undated.length > 0 ? (
        <div>
          <p className="text-portal-micro font-bold uppercase tracking-wide text-portal-muted">Date to be set</p>
          <ul className="mt-2 divide-y divide-portal-line border-t border-portal-line">
            {undated.map((event) => (
              <li key={event.id} className="flex items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="truncate text-portal-small">{titleOf(event)}</p>
                  {event.subtitle ? <p className="truncate text-portal-micro text-portal-muted">{event.subtitle}</p> : null}
                </div>
                <Pill tone={event.stage.tone}>{event.stage.label}</Pill>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
